import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const priceTiers = [
  {
    name: 'B2B',
    pricePerEmployee: [
      { count: '1-5 MA', price: '16 €' },
      { count: '6-20 MA', price: '14 €' },
      { count: '21+ MA', price: '12 €' }
    ],
    description: 'Business to Business – Für KMU & Start-ups',
    featured: false
  },
  {
    name: 'B2K',
    pricePerEmployee: [
      { count: '1-5 MA', price: '15 €' },
      { count: '6-20 MA', price: '13 €' },
      { count: '21+ MA', price: '12 €' }
    ],
    description: 'Business to Kanzleien – Für Steuerkanzleien',
    featured: true
  },
  {
    name: 'B2C',
    pricePerEmployee: [
      { count: 'Alle', price: 'Auf Anfrage' }
    ],
    description: 'Business to Consulting – Komplexe Fälle & Beratung',
    featured: false
  }
]

export default function Prices() {
  const [employeeCount, setEmployeeCount] = useState(5);
  const [selectedTier, setSelectedTier] = useState("b2k");

  const calculateMonthlyCost = (tierName, count) => {
    const tier = priceTiers.find(
      (t) => t.name.toLowerCase() === tierName.toLowerCase(),
    );
    if (!tier || tier.name === "B2C") return "Auf Anfrage";

    const priceRange = tier.pricePerEmployee.find((range) => {
      if (range.count === "Alle") return true;
      const [min] = range.count.split("-").map(Number);
      return count <= (min || 999);
    });

    const price = parseFloat(priceRange.price.replace(" €", ""));
    return `${(price * count).toFixed(0)} € / Monat`;
  };

  return (
    <div>
      <Helmet>
        <title>Preise | Payroll Expert</title>
        <meta
          name="description"
          content="Transparente Preise für Lohnabrechnung bei Payroll Expert. Staffelpreise für Unternehmen und Steuerkanzleien."
        />
      </Helmet>
      <div className="relative isolate overflow-hidden bg-gradient-to-b from-slate-50 via-blue-50 to-slate-100 px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base font-semibold leading-7 tracking-wide text-blue-600">
              Transparente Preise
            </h2>
            <p className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
              Ab 11 € pro Mitarbeiter
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              Keine Einstiegsgebühren. Monatlich kündbar. Staffelpreise für Ihr
              Volumen – passend für B2B, B2K und B2C.
            </p>
          </div>

          {/* Calculator */}
          <div className="mx-auto mt-16 max-w-4xl rounded-2xl bg-white p-8 sm:p-12 shadow-xl ring-1 ring-gray-900/5">
            <div className="grid grid-cols-1 gap-y-8 sm:grid-cols-2 sm:gap-x-12">
              <div>
                <label className="block text-sm font-semibold leading-6 text-gray-900 mb-3">
                  Anzahl Mitarbeiter
                </label>
                <div className="relative">
                  <select
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(Number(e.target.value))}
                    className="block w-full rounded-xl border-0 py-4 px-6 text-lg leading-7 bg-gray-50 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600"
                  >
                    {[1, 3, 5, 10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n} MA
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold leading-6 text-gray-900 mb-3">
                  Ihr Paket
                </label>
                <select
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  className="block w-full rounded-xl border-0 py-4 px-6 text-lg leading-7 bg-gray-50 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600"
                >
                  {priceTiers.map((tier) => (
                    <option key={tier.name} value={tier.name.toLowerCase()}>
                      {tier.name} ({tier.description})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Ergebnis */}
            <div className="mt-12 border-t border-gray-200 pt-12 text-center">
              <h3 className="text-5xl font-bold text-gray-900 mb-4">
                {calculateMonthlyCost(selectedTier, employeeCount)}
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                pro Monat (zzgl. MwSt.)
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-x-2 bg-blue-900 px-8 py-4 text-xl font-bold text-white rounded-2xl hover:bg-blue-700 shadow-xl hover:shadow-2xl transition-all"
              >
                Genau dieses Angebot anfragen
              </Link>
            </div>
          </div>

          {/* Preisvergleichstabelle */}
          <div className="mx-auto mt-24 max-w-6xl">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Preisvergleich pro Mitarbeiter
            </h3>
            <div className="overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-900/5">
              <table className="w-full">
                <thead>
                  <tr className="divide-x divide-gray-900/5 bg-gray-50">
                    <th className="py-4 pl-6 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-8">
                      Paket
                    </th>
                    <th className="hidden py-4 px-4 text-sm font-semibold text-gray-900 lg:table-cell">
                      1-5 MA
                    </th>
                    <th className="hidden py-4 px-4 text-sm font-semibold text-gray-900 lg:table-cell">
                      6-20 MA
                    </th>
                    <th className="hidden py-4 px-4 text-sm font-semibold text-gray-900 lg:table-cell">
                      21+ MA
                    </th>
                    <th className="py-4 pl-4 pr-6 text-right text-sm font-semibold text-gray-900 sm:pr-8">
                      Ideal für
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {priceTiers.map((tier) => (
                    <tr
                      key={tier.name}
                      className={tier.featured ? "bg-yellow-50" : ""}
                    >
                      <td className="py-6 pl-6 pr-4 sm:pl-8">
                        <div className="font-semibold text-gray-900">
                          {tier.name}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          {tier.description}
                        </div>
                      </td>
                      <td className="hidden py-6 px-4 text-sm text-gray-700 lg:table-cell">
                        {tier.pricePerEmployee[0]?.price || "–"}
                      </td>
                      <td className="hidden py-6 px-4 text-sm text-gray-700 lg:table-cell">
                        {tier.pricePerEmployee[1]?.price ||
                          tier.pricePerEmployee[0]?.price ||
                          "–"}
                      </td>
                      <td className="hidden py-6 px-4 text-sm text-gray-700 lg:table-cell">
                        {tier.pricePerEmployee[2]?.price || "–"}
                      </td>
                      <td className="py-6 pl-4 pr-6 text-right text-sm font-medium text-gray-900 sm:pr-8">
                        {tier.featured ? "🥇 Empfohlen" : "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mx-auto mt-20 max-w-4xl">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Preis-FAQ
            </h3>
            <dl className="grid grid-cols-1 gap-y-8 sm:grid-cols-2">
              <div>
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  Mindestlaufzeit?
                </dt>
                <dd className="mt-2 text-sm leading-6 text-gray-600">
                  Keine – monatlich kündbar.
                </dd>
              </div>
              <div>
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  Einstiegsgebühr?
                </dt>
                <dd className="mt-2 text-sm leading-6 text-gray-600">
                  0 € – kostenlose Einrichtung.
                </dd>
              </div>
              <div>
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  Rabatte?
                </dt>
                <dd className="mt-2 text-sm leading-6 text-gray-600">
                  10 % Volumenrabatt ab 50 MA, 15 % ab 100 MA.
                </dd>
              </div>
              <div>
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  MwSt.?
                </dt>
                <dd className="mt-2 text-sm leading-6 text-gray-600">
                  Preise zzgl. 19 % MwSt.
                </dd>
              </div>
            </dl>
          </div>

          {/* Final CTA */}
          <div className="mt-24 flex justify-center">
            <Link
              to="/contact"
              className="group relative overflow-hidden bg-blue-900 px-12 py-6 text-xl font-bold text-white rounded-3xl hover:bg-blue-700 shadow-2xl hover:shadow-3xl transition-all duration-300"
            >
              <span className="relative z-10">
                Persönliches Angebot erhalten
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
